import { SEO } from "../components/SEO";
import { Reveal, Eyebrow } from "../components/Reveal";
import { FIRM } from "../data/firm";

const LegalShell = ({ eyebrow, title, updated, children, path, description, testId }) => (
  <>
    <SEO title={`${title} | ${FIRM.name}`} description={description} path={path} />
    <section className="border-b border-slate-200 bg-slate-50 py-14 lg:py-20">
      <div className="mx-auto max-w-3xl px-5 lg:px-8">
        <Reveal>
          <Eyebrow>{eyebrow}</Eyebrow>
          <h1 className="font-display text-4xl font-semibold text-navy sm:text-5xl">{title}</h1>
          <p className="mt-4 text-xs text-slate-500">Last updated: {updated}</p>
        </Reveal>
      </div>
    </section>
    <div className="mx-auto max-w-3xl px-5 py-14 lg:px-8" data-testid={testId}>
      <Reveal>
        <div className="space-y-8">{children}</div>
      </Reveal>
    </div>
  </>
);

const H = ({ children }) => <h2 className="font-display text-2xl font-semibold text-navy">{children}</h2>;
const P = ({ children }) => <p className="mt-3 text-sm leading-relaxed text-slate-600">{children}</p>;

export const PrivacyPolicy = () => (
  <LegalShell
    eyebrow="Legal"
    title="Privacy Policy"
    updated="July 2026"
    path="/privacy-policy"
    description="Privacy Policy of Aniruddha Sonar & Associates, Chartered Accountants - how information submitted through this website is collected and used."
    testId="privacy-policy-page"
  >
    <section>
      <H>1. Information We Collect</H>
      <P>
        When you use the consultation form or the career application form on this website, we collect the information
        you provide - such as your name, contact details, the service you are interested in, and, for career
        applications, your qualification, experience and resume.
      </P>
    </section>
    <section>
      <H>2. How We Use It</H>
      <P>
        Information submitted is used solely to respond to your enquiry, schedule and conduct consultations, evaluate
        career applications, and meet professional and legal record-keeping obligations. We do not sell, rent or share
        your personal information with third parties for marketing purposes.
      </P>
    </section>
    <section>
      <H>3. Storage and Confidentiality</H>
      <P>
        Submissions and uploaded resumes are stored securely and are accessible only to authorised members of{" "}
        {FIRM.name}. As a Chartered Accountancy practice, the firm observes professional confidentiality obligations
        applicable under the framework of the Institute of Chartered Accountants of India.
      </P>
    </section>
    <section>
      <H>4. External Links</H>
      <P>
        This website links to external websites, including government portals and Google services. Those sites are
        operated by their respective owners and are governed by their own privacy policies.
      </P>
    </section>
    <section>
      <H>5. Your Choices</H>
      <P>
        You may request access to, correction of, or deletion of personal information you have submitted by writing to{" "}
        <a href={`mailto:${FIRM.email}`} className="font-semibold text-corpblue">{FIRM.email}</a>.
      </P>
    </section>
  </LegalShell>
);

export const Disclaimer = () => (
  <LegalShell
    eyebrow="Legal"
    title="Website Disclaimer"
    updated="July 2026"
    path="/disclaimer"
    description="Website disclaimer of Aniruddha Sonar & Associates, Chartered Accountants."
    testId="disclaimer-page"
  >
    <section>
      <H>1. Information Purposes Only</H>
      <P>
        The contents of this website - including articles, service descriptions and FAQs - are intended for general
        information only. They do not constitute professional advice, an opinion, or a client relationship. Laws and
        regulations change and their application depends on specific facts; please seek professional advice before
        acting on any information on this website.
      </P>
    </section>
    <section>
      <H>2. No Solicitation</H>
      <P>
        This website is intended to provide information about {FIRM.name} and its services in accordance with the
        applicable guidelines of the Institute of Chartered Accountants of India. It is not intended to solicit work or
        advertise in any manner prohibited under the applicable professional framework.
      </P>
    </section>
    <section>
      <H>3. External Portals</H>
      <P>
        Links to government portals (such as the Income Tax, GST, MCA, Udyam, Maharashtra Labour and MahaRERA portals)
        and to Google services are provided for convenience. These are external websites operated by their respective
        authorities and organisations; {FIRM.name} does not operate or control them and is not responsible for their
        content or availability.
      </P>
    </section>
    <section>
      <H>4. Accuracy</H>
      <P>
        While reasonable care is taken to keep information accurate and current, the firm makes no warranty regarding
        completeness or accuracy and accepts no liability for actions taken based on the contents of this website.
      </P>
    </section>
  </LegalShell>
);

export const TermsOfUse = () => (
  <LegalShell
    eyebrow="Legal"
    title="Terms of Use"
    updated="July 2026"
    path="/terms-of-use"
    description="Terms of use for the website of Aniruddha Sonar & Associates, Chartered Accountants."
    testId="terms-page"
  >
    <section>
      <H>1. Acceptance</H>
      <P>
        By accessing this website, you agree to these terms of use. If you do not agree, please discontinue use of the
        website.
      </P>
    </section>
    <section>
      <H>2. Use of Content</H>
      <P>
        Content on this website is the property of {FIRM.name} and is provided for your information. You may view and
        print pages for personal, non-commercial use. Reproduction or redistribution without written consent is not
        permitted.
      </P>
    </section>
    <section>
      <H>3. Forms and Submissions</H>
      <P>
        Information you submit through the consultation and career forms must be accurate and lawfully yours to share.
        Submitting a form does not create a client relationship; an engagement is formed only through a confirmed
        engagement arrangement with the firm.
      </P>
    </section>
    <section>
      <H>4. Third-Party Services</H>
      <P>
        Communication links (WhatsApp, phone, email) and external portals are third-party services governed by their
        own terms. The firm is not responsible for the availability or conduct of these services.
      </P>
    </section>
    <section>
      <H>5. Governing</H>
      <P>
        Use of this website is subject to the laws of India, with courts at Mumbai / Navi Mumbai, Maharashtra having
        jurisdiction.
      </P>
    </section>
  </LegalShell>
);
